import { Link } from 'found';
import PropTypes from 'prop-types';
import React from 'react';
import { useIntl } from 'react-intl';
import {
  Badge, Card, CardBody, CardFooter, CardSubtitle, CardText, CardTitle,
} from 'reactstrap';

import TradeRuleTooltip from 'src/components/TradeRule/TradeRuleTooltip';
import Time from 'src/enosikit/components/Time';
import {
  TRADE_DIRECTION_BUY, TRADE_DIRECTION_SELL,
} from 'src/util/constants';
import { getNodesFromEdges } from 'src/util/graphql';
import { useIntlCurrency } from 'src/util/i18n/currency';
import displayUserName from 'src/util/i18n/decorators/displayUserName';
import { valuePerUnitWithStrong, valueRangePerUnitWithStrong } from 'src/util/i18n/energyPrice';
import { getStringAndNumericFormattedDate, getTradeDirection } from 'src/util/i18n/helpers';
import { getPriceRange } from 'src/util/tradeRule';

/**
 * Renders a proposal card with tooltip for time-of-use clauses.
 * @param {object} props - Component props
 * @param {object} props.rule - Trade rule object
 * @param {TRADE_DIRECTION_BUY | TRADE_DIRECTION_SELL} props.direction - Trade direction
 * @param {React.ReactElement} props.actionButton - Action button component
 * @returns {React.ReactElement} Proposal card component
 */
function ProposalCard({ rule, direction, actionButton }) {
  const intl = useIntl();
  const intlCurrency = useIntlCurrency();

  const priceRange = getPriceRange(rule);
  const priceRangeAndUnit = valueRangePerUnitWithStrong(intl, intlCurrency, priceRange);
  const { minimum, maximum } = priceRange;
  const priceAndUnit = valuePerUnitWithStrong(intl, intlCurrency, minimum);
  const clauseNodes = rule.clauses ? getNodesFromEdges(rule.clauses) : [];
  const tradeDirection = getTradeDirection(intl, direction);

  return (
    <Card className="mt-4 mb-4" key={`trade-rules-${rule.uuid}`}>
      <TradeRuleTooltip
        tradeDirection={direction}
        ruleId={rule.uuid}
        clauseNodes={clauseNodes}
      >
        {(cardBodyId) => (
          <CardBody id={cardBodyId} style={clauseNodes.length > 0 ? { cursor: 'help' } : undefined} tabIndex={0}>
            <CardTitle tag="h4">{rule.uuid}</CardTitle>
            <CardSubtitle tag="h5" className="mb-2">
              {intl.formatMessage({ id: 'trade_rule.trade_rule_customer_proposals.trade_rules_buy.proposed.sub_title.text', defaultMessage: 'Proposed' })}
            </CardSubtitle>
            <CardText>
              {direction === TRADE_DIRECTION_BUY
                ? intl.formatMessage(
                  {
                    id: 'trade_rule.trade_rule_customer_proposals.trade_rules_buy.has_proposed_to.text',
                    defaultMessage: '{buyerName} has proposed to buy from {sellerName}',
                  },
                  {
                    buyerName: <strong>{displayUserName(intl, rule.buyer.user)}</strong>,
                    sellerName: <strong>{displayUserName(intl, rule.seller.user)}</strong>,
                  },
                )
                : intl.formatMessage(
                  {
                    id: 'trade_rule.trade_rule_customer_proposals.trade_rules_sell.has_proposed_to.text',
                    defaultMessage: '{sellerName} has proposed to sell to {buyerName}',
                  },
                  {
                    sellerName: <strong>{displayUserName(intl, rule.seller.user)}</strong>,
                    buyerName: <strong>{displayUserName(intl, rule.buyer.user)}</strong>,
                  },
                )}
              <br />
              {intl.formatMessage(
                {
                  id: 'trade_rule.trade_rule_customer_proposals.trade_rules_buy.proposed_by.text',
                  defaultMessage: 'Proposed by {userName} on {timestamp}',
                },
                {
                  userName: <strong>{displayUserName(intl, rule.proposedBy)}</strong>,
                  timestamp: <Time
                    child={getStringAndNumericFormattedDate(intl, rule.proposedAt)}
                    unixTimestamp={rule.proposedAt}
                  />,
                },
              )}
              <br />
              {maximum !== minimum && (
                intl.formatMessage(
                  {
                    id: 'trade_rule.trade_rule_customer_proposals.trade_rules_buy.buy_price.range',
                    defaultMessage: '{tradeDirection} price: {priceRangeAndUnit}',
                  },
                  {
                    tradeDirection,
                    priceRangeAndUnit,
                  },
                )
              )}
              {maximum === minimum && (
                intl.formatMessage(
                  {
                    id: 'trade_rule.trade_rule_customer_proposals.trade_rules_buy.buy_price.flat',
                    defaultMessage: '{tradeDirection} price: {priceAndUnit}',
                  },
                  {
                    tradeDirection,
                    priceAndUnit,
                  },
                )
              )}

              <br />
              <Link
                to={`/trade-rules/${rule.uuid}`}
              >
                <Badge color="mid">{rule.uuid}</Badge>
              </Link>
            </CardText>
          </CardBody>
        )}
      </TradeRuleTooltip>
      <CardFooter>{actionButton}</CardFooter>
    </Card>
  );
}

ProposalCard.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  rule: PropTypes.object.isRequired,
  direction: PropTypes.oneOf([TRADE_DIRECTION_BUY, TRADE_DIRECTION_SELL]).isRequired,
  actionButton: PropTypes.node.isRequired,
};

export default ProposalCard;
